<script setup lang="ts">
import type { NuxtError } from '#app';
import NotFound from '@/shared/ui/Icons/NotFound.vue';
import ServerError from '@/shared/ui/Icons/ServerError.vue';
import { getKindByStatus } from '@/shared/lib/errors';
import {
  ERROR_MESSAGE_BY_KIND,
  ERROR_PAGE_NOT_FOUND_TEXT,
  ERROR_PAGE_SERVER_TEXT,
  ERROR_PAGE_SERVER_TITLE,
  ERROR_PAGE_HOME_BUTTON,
} from '@/shared/constants/errorConstants';

const props = defineProps<{ error: NuxtError }>();

const isMobile = useRuntimeConfig().public.platform === 'mobile';

const kind = computed(() => getKindByStatus(Number(props.error?.statusCode) || 500));

const isNotFound = computed(() => kind.value === 'notFound');
const isServer = computed(() => kind.value === 'server' || kind.value === 'unknown');

const text = computed(() => {
  if (isNotFound.value) return ERROR_PAGE_NOT_FOUND_TEXT;
  if (isServer.value) return ERROR_PAGE_SERVER_TEXT;
  return ERROR_MESSAGE_BY_KIND[kind.value];
});

useSeoMeta({
  title: isServer.value ? ERROR_PAGE_SERVER_TITLE : undefined,
  robots: 'noindex, nofollow',
});

const goHome = (): Promise<void> => clearError({ redirect: '/' });
</script>

<template>
  <NuxtLayout name="default">
    <div :class="['error', { _mobile: isMobile }]" data-testid="error-page">
      <NotFound v-if="isNotFound" class="error__image" />
      <ServerError v-else class="error__image" />
      <span class="error__text">{{ text }}</span>

      <AppButton
        :button-text="ERROR_PAGE_HOME_BUTTON"
        is-small
        data-testid="error-page-home"
        @click="goHome"
      />
    </div>
  </NuxtLayout>
</template>

<style lang="less">
.error {
  .container-d();

  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 48px;
  padding-bottom: 100px;
  color: white;

  &._mobile {
    .container-m();
  }

  &__image {
    width: 778px;
    height: 364px;
    max-width: 100%;
  }

  &__text {
    display: inline-block;
    text-align: center;
    font-size: @size-x4;
    font-weight: 400;
    line-height: 130%;
    color: @color-elephant;
    max-width: 320px;
    margin: 13px 0 78px;
  }
}
</style>
